import type { SchoolYear } from '@bp/bp-graphql-types';
import * as Sentry from '@sentry/react';
import { createContext, type ReactNode, useState } from 'react';
import { type FragmentType, getFragmentData, graphql } from '../client/gql';
import type { OrganizationConfigFieldsFragment } from '../client/gql/graphql';
import { useAuthClaims } from '../hooks/useAuthClaims';

type ContextOrganizationType = {
  uuid: string;
  __typename?: string;
  name?: string;
};

type SchoolYearType = Pick<SchoolYear, 'uuid' | 'name' | 'start' | 'end' | 'foreignRefId'>;

export type OrganizationConfigType = {
  organization: ContextOrganizationType | null;
  setOrganization: (organization: ContextOrganizationType | null) => void;

  currentSchoolYear: SchoolYearType;
  setCurrentSchoolYear: (value: SchoolYearType | ((prevVar: SchoolYearType) => SchoolYearType)) => void;
  schoolyears: SchoolYearType[] | undefined;

  allowStudentDirectMessages: boolean;
  setAllowStudentDirectMessages: (value: boolean | ((prevVar: boolean) => boolean)) => void;
  allowParentExcuses: boolean;
  setAllowParentExcuses: (value: boolean | ((prevVar: boolean) => boolean)) => void;

  bildungsplattformLicense: boolean;
  bildungsplattformVersion: string;
  setBildungsplattformLicense: (value: boolean | ((prevVar: boolean) => boolean)) => void;

  bigBlueButtonLicense: boolean;
  setBigBlueButtonLicense: (value: boolean | ((prevVar: boolean) => boolean)) => void;
  allowRecordings: boolean;
  setAllowRecordings: (value: boolean | ((prevVar: boolean) => boolean)) => void;

  threemaLicense: boolean;
  setThreemaLicense: (value: boolean | ((prevVar: boolean) => boolean)) => void;

  threemaBroadcastLicense: boolean;
  setThreemaBroadcastLicense: (value: boolean | ((prevVar: boolean) => boolean)) => void;

  cloudLicense: boolean;
  setCloudLicense: (value: boolean | ((prevVar: boolean) => boolean)) => void;

  cloudLink: string;
  setCloudLink: (value: string | ((prevVar: string) => string)) => void;

  procuratEnabled: string | null;
  setProcuratEnabled: (value: string | null | ((prevVar: string | null) => string | null)) => void;

  planungEnabled: boolean;
  setPlanungEnabled: (value: boolean | ((prevVar: boolean) => boolean)) => void;

  mailboxLicense: boolean;
  setMailboxLicense: (value: boolean | ((prevVar: boolean) => boolean)) => void;

  organizationProducts: LicensedProductQueryType;
};

export const OrganizationConfigContext = createContext<OrganizationConfigType>({
  organization: null,
  setOrganization: () => {
    return;
  },
  organizationProducts: [],
  currentSchoolYear: { uuid: '', name: '', start: '', end: '' },
  setCurrentSchoolYear() {
    return;
  },
  schoolyears: [],
  allowStudentDirectMessages: false,
  setAllowStudentDirectMessages() {
    return;
  },
  allowParentExcuses: false,
  setAllowParentExcuses() {
    return;
  },
  bildungsplattformLicense: false,
  bildungsplattformVersion: '',
  setBildungsplattformLicense() {
    return;
  },
  bigBlueButtonLicense: false,
  setBigBlueButtonLicense() {
    return;
  },
  allowRecordings: false,
  setAllowRecordings() {
    return;
  },
  cloudLicense: false,
  setCloudLicense() {
    return;
  },
  cloudLink: '',
  setCloudLink() {
    return;
  },
  mailboxLicense: false,
  setMailboxLicense() {
    return;
  },
  threemaBroadcastLicense: false,
  setThreemaBroadcastLicense() {
    return;
  },
  threemaLicense: false,
  setThreemaLicense() {
    return;
  },
  procuratEnabled: null,
  setProcuratEnabled() {
    return;
  },
  planungEnabled: false,
  setPlanungEnabled() {
    return;
  },
});

const OrganizationConfigFieldsDocument = graphql(`
  fragment OrganizationConfigFields on Query {
    schoolYears(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      name
      start
      end
      foreignRefId
    }
    organizations(where: { uuid: $organizationUuid }) {
      uuid
      __typename
      procuratSyncProps
      planungSyncProps
      licensedProducts {
        __typename
        ... on Bildungsplattform {
          uuid
          allowStudentDirectMessages
          allowParentExcuses
          currentSchoolyear
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on Nextcloud {
          uuid
          serverUri
          serverAliase
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on ThreemaBroadcast {
          uuid
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on Threema {
          uuid
          apiToken
          licenseCount
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on BigBlueButton {
          uuid
          __typename
          maxParticipantsPerMeeting
          allowRecording
          apiUrl
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on Zoom {
          uuid
          __typename
          maxParticipantsPerMeeting
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
      }
    }
  }
`);

export type LicensedProductQueryType = OrganizationConfigFieldsFragment['organizations'][number]['licensedProducts'];

export const OrganizationConfigContextProvider = ({
  children,
  query,
}: {
  children: ReactNode;
  query: FragmentType<typeof OrganizationConfigFieldsDocument> | undefined;
}) => {
  const fragment = getFragmentData(OrganizationConfigFieldsDocument, query);
  const schoolYears = fragment?.schoolYears;
  const organizationProducts = fragment?.organizations?.[0].licensedProducts;
  const { pimAuthClaims } = useAuthClaims();
  const [organization, setOrganization] = useState<ContextOrganizationType | null>(pimAuthClaims.getOrganization());

  const [currentSchoolYear, setCurrentSchoolYear] = useState<SchoolYearType>(() => {
    return (
      schoolYears?.find(
        (year) =>
          year.uuid === organizationProducts?.find((p) => p.__typename === 'Bildungsplattform')?.currentSchoolyear,
      ) ?? { uuid: '', name: '', start: '', end: '' }
    );
  });

  const [allowStudentDirectMessages, setAllowStudentDirectMessages] = useState<boolean>(
    (organizationProducts?.filter((p) => p.__typename === 'Bildungsplattform' && p.allowStudentDirectMessages) ?? [])
      .length > 0,
  );

  const [allowParentExcuses, setAllowParentExcuses] = useState<boolean>(
    (organizationProducts?.filter((p) => p.__typename === 'Bildungsplattform' && p.allowParentExcuses) ?? []).length >
      0,
  );

  const [bildungsplattformLicense, setBildungsplattformLicense] = useState(
    (organizationProducts?.filter((p) => p.__typename === 'Bildungsplattform') ?? []).length > 0,
  );
  const [bildungsplattformVersion] = useState(
    organizationProducts?.find((p) => p.__typename === 'Bildungsplattform')?.organizationConnection.edges.find((e) => e)
      ?.properties.version ?? '',
  );
  const [bigBlueButtonLicense, setBigBlueButtonLicense] = useState(
    (organizationProducts?.filter((p) => p.__typename === 'BigBlueButton') ?? []).length > 0,
  );
  const [allowRecordings, setAllowRecordings] = useState(
    (organizationProducts?.filter((p) => p.__typename === 'BigBlueButton') ?? []).shift()?.allowRecording ?? false,
  );
  const [threemaLicense, setThreemaLicense] = useState(
    (organizationProducts?.filter((p) => p.__typename === 'Threema') ?? []).length > 0,
  );
  const [threemaBroadcastLicense, setThreemaBroadcastLicense] = useState(
    (organizationProducts?.filter((p) => p.__typename === 'ThreemaBroadcast') ?? []).length > 0,
  );
  const [cloudLicense, setCloudLicense] = useState(
    (organizationProducts?.filter((p) => p.__typename === 'Nextcloud') ?? []).length > 0,
  );
  const [cloudLink, setCloudLink] = useState<string>(
    organizationProducts?.find((p) => p.__typename === 'Nextcloud')?.serverUri ?? '',
  );
  const [procuratEnabled, setProcuratEnabled] = useState<string | null>(
    fragment?.organizations?.[0].procuratSyncProps
      ? 'P5: ' + JSON.parse(fragment?.organizations?.[0].procuratSyncProps).organizationName
      : null,
  );
  const [mailboxLicense, setMailboxLicense] = useState(false);
  const [planungEnabled, setPlanungEnabled] = useState(!!fragment?.organizations?.[0].planungSyncProps);

  if (import.meta.env.VITE_APP_SENTRY_DSN) {
    Sentry.setUser({
      id: pimAuthClaims.getUser()?.uuid ?? '00000000-0000-0000-0000-000000000000',
      email: pimAuthClaims.getProfile()?.email ?? '',
      segment: organization?.name ?? '',
    });
  }

  return (
    <OrganizationConfigContext.Provider
      value={{
        organization,
        organizationProducts: organizationProducts ?? [],
        setOrganization,
        currentSchoolYear,
        setCurrentSchoolYear,
        schoolyears: schoolYears ?? [],
        allowStudentDirectMessages,
        setAllowStudentDirectMessages,
        allowParentExcuses,
        setAllowParentExcuses,
        bildungsplattformVersion,
        bildungsplattformLicense,
        setBildungsplattformLicense,
        bigBlueButtonLicense,
        setBigBlueButtonLicense,
        allowRecordings,
        setAllowRecordings,
        threemaLicense,
        setThreemaLicense,
        threemaBroadcastLicense,
        setThreemaBroadcastLicense,
        cloudLicense,
        setCloudLicense,
        cloudLink,
        setCloudLink,
        mailboxLicense,
        setMailboxLicense,
        procuratEnabled,
        setProcuratEnabled,
        planungEnabled,
        setPlanungEnabled,
      }}
    >
      {children}
    </OrganizationConfigContext.Provider>
  );
};
